import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import Modal from '../ui/Modal';
import { InvoiceDetailResponse } from '../../types/invoiceDetail';
import { invoiceService } from '../../services/invoiceService';
import { requireArenaSlug } from '../../utils/arena';

interface InvoiceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber: string;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  onClose,
  invoiceNumber,
}) => {
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (isOpen && invoiceNumber) {
      fetchInvoiceDetail();
    }
  }, [isOpen, invoiceNumber]);

  const fetchInvoiceDetail = async () => {
    try {
      setLoading(true);
      setError('');

      const arenaSlug = requireArenaSlug();
      const response = await invoiceService.getInvoiceDetail(arenaSlug, invoiceNumber);
      setInvoiceDetail(response);
    } catch (error: any) {
      console.error('Error fetching invoice detail:', error);
      setError(error.message || 'Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <Icon icon="mdi:loading" className="w-6 h-6 animate-spin text-blue-600" />
            <span>Loading invoice details...</span>
          </div>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <div className="flex flex-col items-center justify-center py-12">
          <Icon icon="mdi:alert-circle" className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-600 text-center mb-4">{error}</p>
          <button
            onClick={fetchInvoiceDetail}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Modal>
    );
  }

  if (!invoiceDetail) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <div className="flex items-center justify-center py-12">
          <span>No invoice data available</span>
        </div>
      </Modal>
    );
  }

  const { data: invoice } = invoiceDetail;

  // Combine field bookings and product items into one table
  const getAllItems = () => {
    interface TableItem {
      type: string;
      name: string;
      quantity: number;
      unitPrice: number;
      subtotal: number;
    }

    const items: TableItem[] = [];

    const isDP =
      invoice.payment_terms?.toLowerCase() === 'dp' ||
      invoice.status?.toLowerCase() === 'partial' ||
      invoice.payment_type === 'dp_balance';

    // Add field bookings
    if (invoice.field_bookings) {
      invoice.field_bookings.forEach((booking) => {
        const itemPrice =
          isDP &&
          invoice.paid_amount > 0 &&
          invoice.total_booking_amount &&
          invoice.total_booking_amount > invoice.paid_amount
            ? booking.final_price * (invoice.paid_amount / invoice.total_booking_amount)
            : booking.final_price;

        items.push({
          type: 'field',
          name: isDP ? `${booking.field_name} (DP)` : booking.field_name,
          quantity: booking.total_slots || 1,
          unitPrice: itemPrice,
          subtotal: itemPrice,
        });
      });
    }

    // Add product items
    if (invoice.product_items) {
      invoice.product_items.forEach((product) => {
        items.push({
          type: 'product',
          name: product.product_name,
          quantity: product.quantity,
          unitPrice: product.unit_price,
          subtotal: product.total_price,
        });
      });
    }

    return items;
  };

  const isDP =
    invoice.payment_terms?.toLowerCase() === 'dp' ||
    invoice.status?.toLowerCase() === 'partial' ||
    invoice.payment_type === 'dp_balance';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" className="max-w-2xl">
      <div className="bg-white">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <img src="https://cdn.sporto.id/sporto-black.png" alt="Sporto" />
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">ID : #{invoice.invoice_number}</p>
              <p className="text-sm text-gray-600">
                Date :{' '}
                {new Date(invoice.created_at).toLocaleDateString('id-ID', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center mb-4">
            <div className="w-full">
              <div
                className={`text-center py-3 px-6 rounded-lg w-full font-semibold text-lg tracking-wide ${
                  invoice.status?.toLowerCase() === 'paid'
                    ? 'bg-green-500 text-white'
                    : ['draft', 'sent', 'partial'].includes(invoice.status?.toLowerCase())
                      ? 'bg-yellow-500 text-white'
                      : 'bg-red-500 text-white'
                }`}
              >
                {invoice.status == 'paid'
                  ? 'Paid'
                  : ['draft', 'sent', 'partial'].includes(invoice.status?.toLowerCase())
                    ? 'Pending'
                    : 'Unpaid'}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
          <div className="flex justify-between">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Icon icon="mdi:account" className="w-5 h-5 text-gray-600" />
                <span className="text-gray-900">{invoice.customer_name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon icon="mdi:phone" className="w-5 h-5 text-gray-600" />
                <span className="text-gray-900">{invoice.customer_phone}</span>
              </div>
              {invoice.customer_email && (
                <div className="flex items-center space-x-2">
                  <Icon icon="mdi:email" className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-900">{invoice.customer_email}</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-600">Arena Name</p>
              <p className="text-gray-900 font-semibold">{invoice.arena_name}</p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Item</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Qty</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                    Price
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {getAllItems().map((item, index) => (
                  <tr key={index} className="bg-white">
                    <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-900">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      {invoiceService.formatCurrency(item.unitPrice)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      {invoiceService.formatCurrency(item.subtotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="px-6 pb-6">
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rincian Biaya</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Metode Pembayaran</span>
                <span className="text-gray-900">{invoice.payment_method || 'Ori'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">
                  {invoiceService.formatCurrency(invoice.subtotal)}
                </span>
              </div>
              {isDP && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Down Payment (DP)</span>
                  <span className="text-gray-900">
                    {invoiceService.formatCurrency(invoice.paid_amount || invoice.total_amount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Biaya Platform</span>
                <span className={invoice.service_fee > 0 ? 'text-red-500 font-medium' : 'text-gray-900'}>
                  {invoice.service_fee > 0
                    ? `- ${invoiceService.formatCurrency(invoice.service_fee)}`
                    : 'Rp. 0'}
                </span>
              </div>
              {invoice.discount_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount</span>
                  <span className="text-gray-900">
                    -{invoiceService.formatCurrency(invoice.discount_amount)}
                  </span>
                </div>
              )}
              <div className="border-t pt-2">
                <div className="flex justify-between text-lg font-semibold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-primary-600">
                    {invoiceService.formatCurrency(invoice.merchant_receives || invoice.total_amount)}
                  </span>
                </div>
              </div>
              {isDP && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Sisa Pembayaran</span>
                  <span className="text-gray-900">
                    {invoiceService.formatCurrency(
                      invoice.remaining_amount > 0
                        ? invoice.remaining_amount
                        : invoice.total_booking_amount && invoice.paid_amount
                        ? Math.max(0, invoice.total_booking_amount - invoice.paid_amount)
                        : 0
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Print Button */}
        <div className="px-6 pb-6">
          <div className="flex justify-end">
            <button
              onClick={async () => {
                // download PDF
                try {
                  setDownloading(true);
                  const arenaSlug = requireArenaSlug();
                  const blob = await invoiceService.downloadInvoicePdf(
                    arenaSlug,
                    invoice.invoice_number
                  );

                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `invoice-${invoice.invoice_number}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('Failed to download invoice PDF:', err);
                  alert('Gagal mendownload invoice. Silakan coba lagi.');
                } finally {
                  setDownloading(false);
                }
              }}
              disabled={downloading}
              className={`w-40 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-4 px-2 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 ${
                downloading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              <Icon icon="mdi:printer" className="w-5 h-5" />
              <span>{downloading ? 'Downloading...' : 'Print Invoice'}</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceDetailModal;
